import React, { PropsWithChildren } from 'react'
import clsx from 'clsx'
import { Text as TextStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import useDeprecatedComponent from '../../lib/hooks/useDeprecatedComponent'
import { CommonProps } from '../helperTypes'

type Props = PropsWithChildren<{
  /**
   * The typography color
   * @default determined from the `variant` prop
   */
  color?: 'heading' | 'primary' | 'secondary' | 'tertiary'
  /**
   * The html tag to be used
   * @default determined from the `variant` prop
   */
  component?: string
  /**
   * The typography variant
   * @default medium
   */
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'large' | 'medium' | 'small' | 'xsmall'
  /** If `true`, remove bottom margin */
  noGutterBottom?: boolean
}>

type DeprecatedProps = {
  dark?: boolean
  tag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

export type TypographyProps = Props & DeprecatedProps & CommonProps

const useStyles = createUseStyles(TextStyles, 'typography')

const variantMapping: any = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  large: 'div',
  medium: 'div',
  small: 'div',
  xsmall: 'div',
}

const colorMapping: any = {
  h1: 'heading',
  h2: 'heading',
  h3: 'heading',
  h4: 'heading',
  h5: 'heading',
  h6: 'heading',
  large: 'primary',
  medium: 'primary',
  small: 'primary',
  xsmall: 'tertiary',
}

const Typography = (props: TypographyProps) => {
  const {
    children,
    className,
    color: colorProp,
    component,
    dark,
    noGutterBottom = false,
    tag,
    variant = 'medium',
    ...rest
  } = props
  const classes = useStyles()
  useDeprecatedProps('Typography', { dark: undefined, tag: 'variant' }, props)
  const color = colorProp || colorMapping[variant]
  const Component = tag || component || variantMapping[variant] || 'span'

  return (
    <Component
      {...rest}
      className={clsx(className, classes[`_${variant}`], {
        [classes.root]: variant.indexOf('h') < 0,
        [classes._noGutter]: noGutterBottom,
        [classes._heading]: color === 'heading',
        [classes._body]: color === 'primary',
        [classes._label]: color === 'secondary',
        [classes._legend]: color === 'tertiary',
      })}
    >
      {children}
    </Component>
  )
}

export default Typography

// retro compatibility
export const Title = ({ children, ...rest }: TypographyProps) => {
  useDeprecatedComponent('Title', 'Please use Typography.')
  return <Typography {...rest}>{children}</Typography>
}
